import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Pencil, X } from "lucide-react";
import { DEPENDENT_RELATIONSHIP_OPTIONS } from "@shared/constants/dependentRelationships";

// The shared canonical set, plus one import-only edge case the bulk-import
// migration tool needs (DP's child distinction). Don't surface it elsewhere.
const RELATIONSHIPS = [
  ...DEPENDENT_RELATIONSHIP_OPTIONS,
  { value: "child_domestic_partner", label: "Domestic Partner's Child" },
] as const;

export interface EditableDependent {
  employeeEid?: string | null;
  employeeFirstName?: string;
  employeeLastName?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  relationship?:
    | "spouse"
    | "spouse_ex"
    | "domestic_partner"
    | "child"
    | "child_adopted"
    | "child_step"
    | "child_domestic_partner";
  ssn?: string | null;
  dateOfBirth?: string | null;
  sex?: "male" | "female" | "other" | "unknown";
  nameInferredFromEmployee?: boolean;
  [k: string]: unknown;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: number;
  dependents: EditableDependent[];
  index: number;
}

function str(v: string | null | undefined): string {
  return v == null ? "" : String(v);
}

export function EditDependentModal({
  open,
  onOpenChange,
  jobId,
  dependents,
  index,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const initial = dependents[index] ?? ({} as EditableDependent);
  const [draft, setDraft] = useState<EditableDependent>(initial);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(dependents[index] ?? ({} as EditableDependent));
      setIsEditing(false);
    }
  }, [open, index, dependents]);

  const save = useMutation({
    mutationFn: async () => {
      const next = dependents.map((d, i) => (i === index ? draft : d));
      const res = await apiRequest(
        "PATCH",
        `/api/migration/jobs/${jobId}/census/dependents`,
        { dependents: next },
      );
      return await res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/migration/jobs/${jobId}`] });
      toast({ title: "Dependent saved" });
      setIsEditing(false);
    },
    onError: (err: any) =>
      toast({
        title: "Save failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  function set<K extends keyof EditableDependent>(
    field: K,
    value: EditableDependent[K],
  ) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function setStr<K extends keyof EditableDependent>(field: K) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      set(field, (v === "" ? null : v) as EditableDependent[K]);
    };
  }

  const d = isEditing ? draft : (dependents[index] ?? ({} as EditableDependent));
  const fmtSex = (s: typeof d.sex) =>
    s === "male" ? "Male" : s === "female" ? "Female" : s === "other" ? "Other" : "—";
  const relLabel = (r: typeof d.relationship) =>
    RELATIONSHIPS.find((x) => x.value === r)?.label || str(r) || "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0">
        <DialogHeader className="border-b p-6 pb-4">
          <DialogTitle>
            {d.firstName} {d.lastName}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              ({relLabel(d.relationship)} of {d.employeeFirstName}{" "}
              {d.employeeLastName})
            </span>
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Editing the migration staging copy. If census is already confirmed, edits are written through to the imported dependent row (matched by name+relationship)."
              : "Full record from the source file. Click Edit below to make changes."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field
              label="First name"
              editing={isEditing}
              display={str(d.firstName)}
            >
              <Input value={str(draft.firstName)} onChange={setStr("firstName")} />
            </Field>
            <Field label="Last name" editing={isEditing} display={str(d.lastName)}>
              <Input value={str(draft.lastName)} onChange={setStr("lastName")} />
            </Field>
            <Field
              label="Middle name"
              editing={isEditing}
              display={str(d.middleName)}
            >
              <Input
                value={str(draft.middleName)}
                onChange={setStr("middleName")}
              />
            </Field>
            <Field
              label="Relationship"
              editing={isEditing}
              display={relLabel(d.relationship)}
            >
              <Select
                value={str(draft.relationship) || ""}
                onValueChange={(v) => set("relationship", v as any)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Date of birth"
              editing={isEditing}
              display={str(d.dateOfBirth)}
            >
              <Input
                type="date"
                value={str(draft.dateOfBirth)}
                onChange={setStr("dateOfBirth")}
              />
            </Field>
            <Field label="Sex" editing={isEditing} display={fmtSex(d.sex)}>
              <Select
                value={str(draft.sex) || "unknown"}
                onValueChange={(v) => set("sex", v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="SSN" editing={isEditing} display={str(d.ssn)}>
              <Input value={str(draft.ssn)} onChange={setStr("ssn")} />
            </Field>
          </div>

          {d.nameInferredFromEmployee && (
            <p className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              This dependent's identity wasn't in the source file (Ease minimal
              format reuses the employee's name). Fix first and last name here
              before confirming.
            </p>
          )}
        </div>

        <DialogFooter className="border-t bg-background p-4">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setDraft(dependents[index] ?? ({} as EditableDependent));
                }}
                disabled={save.isPending}
              >
                <X className="mr-1 h-3 w-3" /> Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  editing,
  display,
}: {
  label: string;
  children?: React.ReactNode;
  editing?: boolean;
  display?: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {editing ? (
        children
      ) : (
        <div className="text-sm">
          {display !== undefined && display !== null && display !== "" ? (
            display
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      )}
    </div>
  );
}
